import { useCallback } from 'react';

import { ImageListType } from 'react-images-uploading';

const VALID_MIME_TYPES = new Set([
  'image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/bmp',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska',
  'application/pdf',
]);

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const MMS_IMAGE_SIZE = 1080;
const MMS_IMAGE_QUALITY = 0.85;

const MAX_VIDEO_FILE_SIZE_MB = 500;
const MAX_VIDEO_FILE_SIZE = MAX_VIDEO_FILE_SIZE_MB * 1024 * 1024;

const isValidFileType = (mimeType: string): boolean => VALID_MIME_TYPES.has(mimeType.toLowerCase());

type UseFilePasteOptions = {
  attachments: ImageListType;
  setAttachments: (files: ImageListType) => void;
  maxUploads: number;
};

type UseFilePasteResult = {
  handlePaste: (event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

const optimizeImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { width, height } = img;
      const scale = Math.min(1, MMS_IMAGE_SIZE / Math.max(width, height));
      const newWidth = Math.round(width * scale);
      const newHeight = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const optimizedFile = new File([blob], file.name.replace(/\.png$/i, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(optimizedFile);
        },
        'image/jpeg',
        MMS_IMAGE_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

const extractFilesFromClipboard = async(
  clipboardData: DataTransfer,
): Promise<Array<{file: File; dataURL: string}>> => {
  const files: Array<{file: File; dataURL: string}> = [];
  const processedFiles = new Set<string>();

  const processFile = async(file: File) => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    if (processedFiles.has(fileKey)) {
      return;
    }
    processedFiles.add(fileKey);

    if (!isValidFileType(file.type)) {
      return;
    }

    const maxSize = file.type.startsWith('video/') ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
    if (file.size > maxSize) {
      return;
    }

    try {
      let processedFile = file;

      if (file.type === 'image/png') {
        processedFile = await optimizeImage(file);
      }

      const dataURL = await readFileAsDataURL(processedFile);
      files.push({ file: processedFile, dataURL });
    } catch {
      // Skip files that fail to read
    }
  };

  for (const file of Array.from(clipboardData.files)) {
    await processFile(file);
  }

  for (const item of Array.from(clipboardData.items)) {
    const file = item.getAsFile();
    if (file) {
      await processFile(file);
    }
  }

  return files;
};

export const useFilePaste = ({
  attachments,
  setAttachments,
  maxUploads,
}: UseFilePasteOptions): UseFilePasteResult => {
  const handlePaste = useCallback(
    async(event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      const hasSupportedFiles =
        Array.from(clipboardData.files).some((file) => isValidFileType(file.type)) ||
        Array.from(clipboardData.items).some((item) => isValidFileType(item.type));

      if (!hasSupportedFiles) {
        return;
      }

      event.preventDefault();

      const availableSlots = maxUploads - attachments.length;
      if (availableSlots <= 0) {
        return;
      }

      const pastedFiles = await extractFilesFromClipboard(clipboardData);
      if (pastedFiles.length === 0) {
        return;
      }

      const filesToAdd = pastedFiles.slice(0, availableSlots);
      setAttachments([...attachments, ...filesToAdd]);
    },
    [attachments, setAttachments, maxUploads],
  );

  return { handlePaste };
};
